<template>
  <TransitionRoot
    as="template"
    :show="isOpen"
    @after-leave="onAfterLeave"
  >
    <Dialog
      as="div"
      class="fixed inset-0 z-50 flex items-center justify-center"
      @close="onCancel"
    >
      <div
        class="fixed inset-0 bg-black bg-opacity-50"
        @click.stop="onCancel"
      />
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0 scale-95"
        enter-to="opacity-100 scale-100"
        leave="ease-in duration-200"
        leave-from="opacity-100 scale-100"
        leave-to="opacity-0 scale-95"
      >
        <div 
          class="bg-white rounded-lg shadow-xl max-w-xl w-full z-50"
          @click.stop
        >
          <div class="p-6 flex flex-row gap-4">
            <div :class="['size-10 flex-shrink-0 mx-auto flex items-center justify-center rounded-full border-[6px]', iconBgClass]">
              <ExclamationCircleIcon
                :class="['size-5 rounded-full', iconColorClass]"
                aria-hidden="true"
              />
            </div>
            <span class="w-full">
              <h3 class="text-lg font-medium text-gray-900">{{ state.title }}</h3>
              <p class="text-sm text-gray-600">{{ state.description }}</p>
            </span>
          </div>
          <div class="p-4 sm:p-6 sm:py-3 pt-3 pb-3 bg-gray-50 rounded-b-lg flex justify-end gap-3">
            <CButton
              :btn-title="state.cancelText"
              btn-type="gray-outline-btn"
              :disabled="state.loading"
              @click="onCancel"
            />
            <CButton
              :btn-title="state.confirmText"
              :btn-type="state.btnVariant"
              :is-loading="state.loading"
              :disabled="state.loading"
              @click="onConfirm"
            />
          </div>
        </div>
      </TransitionChild>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { resetConfirmationState, useConfirmationState } from '@/shared/common/composables/useConfirmationModal';
import { Dialog, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { ExclamationCircleIcon } from '@heroicons/vue/outline';
import { computed } from 'vue';

const state = useConfirmationState();
const isOpen = computed(() => state.visible);

const iconColorClass = computed(() => ({
  success: 'text-green-600',
  primary: 'text-blue-600',
  danger: 'text-red-600',
  warning: 'text-yellow-600'
}[state.variant]));

const iconBgClass = computed(() => ({
  success: 'bg-green-100 border-green-50',
  primary: 'bg-blue-100 border-blue-50',
  danger: 'bg-red-100 border-red-50',
  warning: 'bg-yellow-100 border-yellow-50'
}[state.variant]));

function onCancel() {
  if (!state.loading) {
    state.reject?.();
    // Just hide the modal, don't reset state yet
    state.visible = false;
  }
}

// This runs after the closing transition completes
function onAfterLeave() {
  resetConfirmationState();
}

async function onConfirm() {
  if (state.loading) return;
  
  state.loading = true;
  
  try {
    await state.loadingFn();
    state.resolve?.();
    // Just hide the modal, reset will happen in onAfterLeave
    state.visible = false;
  } catch (error) {
    console.error('Action failed:', error);
    state.loading = false; // Only reset loading on error, keep modal open
  }
}
</script>